import { createFileRoute, Outlet, useNavigate, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useState } from 'react';

import { isNotFound, isPermissionDenied } from '../../../../api/call.js';
import { conversationsQueryKey, useConversations } from '../../../../api/conversations.js';
import { usePermissions } from '../../../../api/me.js';
import { ConversationList } from '../../../../components/ConversationList.js';
import styles from '../../../../components/conversations.module.css';
import { UsageMeter } from '../../../../billing/UsageMeter.js';
import { PageHeading } from '../../../../components/PageHeading.js';
import { PermissionDeniedPage, WebsiteNotFoundPage } from '../../../../components/SectionStates.js';
import { WebsiteStatusContext } from '../../../../components/WebsiteSection.js';
import { conversationCount } from '../../../../conversations/display.js';
import {
  ConversationsSectionContext,
  type ConversationsSection,
} from '../../../../conversations/section.js';
import { parseConversationsSearch, searchForPage } from '../../../../conversations/search.js';
import { useIsListDetailPane } from '../../../../frame/breakpoints.js';

/**
 * The Conversations section (`features/website-conversations/technical.md`,
 * Routing). This layout holds the page header and, at 1180px and wider, the
 * list beside a pane its child routes fill; below 1180px the child routes are
 * the whole view — the list, or one conversation with "Back to conversations"
 * (`patterns/ui-patterns.md`, 5).
 *
 * The page number lives in the URL (`patterns/frontend-conventions.md`, 1);
 * `parseConversationsSearch` turns anything that isn't a page into page 1.
 */
export const Route = createFileRoute('/_app/websites/$websiteId/conversations')({
  validateSearch: parseConversationsSearch,
  component: ConversationsLayout,
});

const TITLE = 'Conversations';
const PERMISSION = 'conversations and marks';

function ConversationsLayout() {
  const permissions = usePermissions();
  const websiteStatus = useContext(WebsiteStatusContext);

  // The nav already hides the section; this covers an opened URL, and a
  // permission removed while signed in once `me` has been refetched
  // (`patterns/ui-patterns.md`, 3). No conversation is requested.
  if (!permissions.includes('conversations_and_marks')) {
    return <PermissionDeniedPage title={TITLE} permission={PERMISSION} />;
  }
  if (websiteStatus === 'not-found') return <WebsiteNotFoundPage title={TITLE} />;

  return <ConversationsSectionView />;
}

function ConversationsSectionView() {
  const { websiteId } = Route.useParams();
  const { page = 1 } = Route.useSearch();
  const { conversationId } = useParams({ strict: false });
  const navigate = useNavigate();
  const isPane = useIsListDetailPane();
  const [openedId, setOpenedId] = useState<string | null>(null);
  const list = useConversations(websiteId, { page });
  const queryClient = useQueryClient();

  // A page past the last one is answered with the last page (technical.md,
  // Clamp page number). The URL is corrected to the page shown, replacing the
  // entry, so the range, Previous and Next and every link agree with the rows.
  // The answer is kept for the corrected page, so the rows don't reload.
  const shownPage = list.data?.page;
  useEffect(() => {
    if (shownPage === undefined || shownPage === page || !list.data) return;
    queryClient.setQueryData(conversationsQueryKey(websiteId, { page: shownPage }), list.data);
    void navigate({ to: '.', search: searchForPage(shownPage), replace: true });
  }, [list.data, navigate, page, queryClient, shownPage, websiteId]);

  const section = useMemo<ConversationsSection>(
    () => ({
      websiteId,
      page,
      goToPage: (next) => {
        void navigate({ to: '.', search: searchForPage(next) });
      },
      openedId,
      setOpenedId,
    }),
    [navigate, openedId, page, websiteId],
  );

  if (list.isError && isPermissionDenied(list.error)) {
    return <PermissionDeniedPage title={TITLE} permission={PERMISSION} />;
  }
  if (list.isError && isNotFound(list.error)) return <WebsiteNotFoundPage title={TITLE} />;

  // In the pane, focus stays on the row, so there is nothing to return to; and
  // each row opened there is its own history entry, so Back isn't the list.
  const forgetOpened = () => setOpenedId(null);

  // An open conversation as its own view takes focus itself.
  const isOwnViewOpen = !isPane && conversationId !== undefined;
  const isEmpty = list.data?.total === 0;

  return (
    <ConversationsSectionContext.Provider value={section}>
      <div className={styles.header}>
        <PageHeading documentTitle={TITLE} focusOnMount={!isOwnViewOpen}>
          {TITLE}
        </PageHeading>
        <p className={styles.caption}>{list.data ? conversationCount(list.data.total) : null}</p>
        {list.data ? <UsageMeter usage={list.data.usage} compact /> : null}
      </div>
      {isPane && !(isEmpty && conversationId === undefined) ? (
        <div className={styles.split}>
          <ConversationList
            websiteId={websiteId}
            page={page}
            onPageChange={section.goToPage}
            selectedId={conversationId}
            onOpen={forgetOpened}
          />
          <div className={styles.pane}>
            <Outlet />
          </div>
        </div>
      ) : isPane ? (
        <div className={styles.standalone}>
          <ConversationList
            websiteId={websiteId}
            page={page}
            onPageChange={section.goToPage}
            onOpen={forgetOpened}
          />
        </div>
      ) : (
        <Outlet />
      )}
    </ConversationsSectionContext.Provider>
  );
}
